<template>
  <div>
    <div class="d-flex justify-content-between">
      <h1 class="page-header">{{ $t('user.pageTitle') }}</h1>
      <ts-breadcrumb
        :items="[
          { text: $t('home'), href: '/' },
          {
            text: $t('user.pageTitle'),
            href: '/users'
          },
          {
            text: $t('create'),
            active: true
          }
        ]"
      />
    </div>
    <ts-card>
      <ts-card-wrapper>
        <div class="ts-grid ts-grid-cols-1 md:ts-grid-cols-3 ts-gap-4">
          <Profile
            v-model="model"
            :validate="errors"
            class="ts-w-full ts-border ts-p-4 ts-rounded-lg ts-border-gray-200"
          />
          <div class="ts-space-y-4">
            <div class="ts-border ts-border-gray-200 ts-p-4 ts-rounded-lg">
              <RoleList v-model="model" />
            </div>
            <div
              class="ts-border ts-border-gray-200 ts-p-4 ts-rounded-lg"
              style="min-height: 194px"
            >
              <div class="ts-rounded-sm ts-p-2">
                <div
                  class="ts-flex ts-justify-between ts-border-b ts-border-gray-200 ts-pb-1"
                >
                  <p class="ts-font-bold ts-mb-0">{{ $t('user.allowIp') }}</p>
                  <a
                    href="#"
                    @click.prevent="addIp"
                    class="hover:ts-no-underline ts-text-right"
                  >
                    <i class="fa fa-plus"></i>
                  </a>
                </div>
                <div
                  class="ts-grid xl:ts-grid-cols-4 xl:ts-gap-2 ts-gap-2 ts-mt-2"
                >
                  <template v-for="(item, index) in model.allow_ip_address">
                    <div class="ts-col-span-3" :key="index">
                      <input
                        v-model.trim="item.ip"
                        type="text"
                        class="ts-border-b ts-border-gray-400 ts-w-full ts-outline-none ts-px-3"
                        :class="{
                          'is-invalid': errors.has(
                            'allow_ip_address.' + index + '.ip'
                          )
                        }"
                      />
                      <div
                        class="invalid-feedback"
                        v-if="errors.has('allow_ip_address.' + index + '.ip')"
                      >
                        {{ errors.first('allow_ip_address.' + index + '.ip') }}
                      </div>
                    </div>
                    <a
                      href="#"
                      class="ts-text-right ts-my-auto ts-text-red-600 hover:ts-text-red-600"
                      @click.prevent="removeIp(index)"
                    >
                      <i class="fa fa-trash"></i>
                    </a>
                  </template>
                </div>
              </div>
            </div>
          </div>
          <div class="ts-border ts-border-gray-200 ts-p-4 ts-rounded-lg">
            <Branch v-model="model" :validate="errors" />
          </div>
        </div>
        <ReachableWarehouse v-model="model" />
        <div class="ts-mt-4 ts-p-4 ts-border ts-rounded-lg ts-border-gray-200">
          <PermissionComponent v-model="model" />
        </div>
        <div class="ts-flex ts-justify-end ts-space-x-3 ts-mt-4">
          <ts-button @click.prevent="$router.push({ name: 'user' })">{{
            $t('cancel')
          }}</ts-button>
          <ts-button
            :waiting="waiting"
            @click.prevent="onUpdate"
            color="primary"
            >{{ $t('update') }}</ts-button
          >
        </div>
      </ts-card-wrapper>
    </ts-card>
  </div>
</template>

<script>
import { Errors } from 'form-backend-validation'
import { transform } from 'lodash'
import { mapState } from 'vuex'
import Branch from './components/branch'
import Employee from './components/employee'
import PermissionComponent from './components/permission-component'
import Profile from './components/profile'
import ReachableWarehouse from './components/reachable-warehouse.vue'
import RoleList from './components/role-component'

export default {
  name: 'UserCreateEdit',
  components: {
    RoleList,
    PermissionComponent,
    Profile,
    Employee,
    Branch,
    ReachableWarehouse
  },
  data () {
    return {
      loading: false,
      waiting: false,
      errors: new Errors(),
      model: {
        user_id: 0,
        username: null,
        name: null,
        disable: false,
        password: null,
        password_confirmation: null,
        employee_id: null,
        stock_alert: false,
        allow_only_scan_on_transfer: false,
        is_admin_password: false,
        branches: [],
        roles: [],
        permissions: [],
        allow_ip_address: [],
        reachable_warehouses: [],
        mobile_role_id: null,
        parent_user_id: null,
        counter_id: null,
        telegram_user: undefined
      }
    }
  },
  created () {
    this.setEditData()
  },
  computed: {
    ...mapState('security/user', ['edit_data'])
  },
  methods: {
    // ...mapActions('security/user', ['getDealer']),
    onUpdate () {
      this.errors = new Errors()
      this.waiting = true
      this.$store
        .dispatch('security/user/update', {
          id: this.model.user_id,
          ...this.model
        })
        .then(response => {
          this.$toasted.success(response.message)
          this.$router.push({ name: 'user' })
        })
        .catch(error => {
          this.errors = new Errors(error.errors)
          this.$toasted.error(error.message)
        })
        .finally(() => {
          this.waiting = false
        })
    },
    setEditData () {
      if (!_.isEmpty(this.edit_data)) {
        this.model.user_id = this.edit_data.user_id
        this.model.username = this.edit_data.user_name
        this.model.name = this.edit_data.name
        this.model.disable = this.edit_data.disable
        this.model.employee_id = this.edit_data.employee_id
        this.model.stock_alert = this.edit_data.stock_alert
        this.model.allow_only_scan_on_transfer = this.edit_data.allow_only_scan_on_transfer
        this.model.is_admin_password = this.edit_data.is_admin_password
        this.model.mobile_role_id = this.edit_data.mobile_role_id
        this.model.parent_user_id = this.edit_data.parent_user_id
        this.model.counter_id = this.edit_data.user_counter_id
        this.model.telegram_user = this.edit_data.user_telegram_user

        this.model.roles = this.edit_data.roles[0].id
        this.model.reachable_warehouses = this.edit_data.reachable_warehouses.map(
          el => el.location_id
        )
        this.model.permissions = this.edit_data.permissions.map(el => el.id)
        this.model.branches = transform(
          this.edit_data.branches,
          (result, branch) => {
            result[branch.branch_id] = {
              warehourse: transform(
                this.edit_data.warehouses.filter(
                  b => b.branch_id == branch.branch_id
                ),
                (result, warehourse) => {
                  result[warehourse.location_id] = {
                    is_default: warehourse.pivot.is_default
                  }
                },
                {}
              ),
              is_default: branch.pivot.is_default
            }
          },
          {}
        )
        if (this.edit_data.allow_ip_address !== 'Any') {
          this.model.allow_ip_address = this.edit_data.allow_ip_address
            .split('|')
            .map(el => ({
              ip: el
            }))
        }

        // this.model.branches = transform(this.edit_data.branches, function(result, branch){
        //     result[branch.branch_id] = {
        //       is_default: Boolean(parseInt(branch.pivot.is_default)),
        //       warehourse: {}
        //       // transform(
        //       //   this.edit_data.warehouses.filter(b => b.branch_id == branch.branch_id),
        //       //   function(result, warehourse){
        //       //     result[warehourse.location_id] = {
        //       //       is_default: Boolean(parseInt(warehourse.pivot.is_default)),
        //       //     }
        //       //   },
        //       //   {}
        //       // )
        //     }
        // }, {})
      }
    },
    addIp () {
      this.model.allow_ip_address.push({
        ip: null
      })
    },
    removeIp (index) {
      this.model.allow_ip_address.splice(index, 1)
    }
  },
  beforeRouteLeave (to, from, next) {
    this.$store.commit('security/user/SET_EDIT_DATA', {})
    this.$store.commit('security/user/SET_BRANCHESE', [])
    next()
  }
}
</script>
